import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, ChevronLeft, ChevronRight, Users, Briefcase, GraduationCap, CheckCircle, Calendar, Clock, MapPin, DollarSign, Target } from "lucide-react";
import janetLogo from "@/assets/janet-marketing-logo.png";
import posterGeneral from "@/assets/poster-general.jpeg";
import posterVirtual from "@/assets/poster_virtual.jpeg";
import posterHybrid from "@/assets/poster-hybrid.jpeg";

interface HeroSectionProps {
  onEnrollClick: () => void;
}

const TRAINING_START_DATE = new Date('2026-02-17T20:00:00+03:00');

const POSTERS = [
  { 
    id: 'hybrid', 
    title: 'Hybrid Training', 
    image: posterHybrid,
    badge: 'KES 25,000',
    popular: true
  },
  { 
    id: 'virtual', 
    title: 'Virtual Training', 
    image: posterVirtual,
    badge: 'KES 13,000'
  },
  { 
    id: 'general', 
    title: 'Full Curriculum', 
    image: posterGeneral,
    badge: 'Complete Guide'
  }
];

// Enhanced Context - WHO, WHAT, WHY, HOW, WHEN
const CONTEXT_INFO = [
  {
    question: "WHO IS THIS FOR?",
    icon: Users,
    answer: "Aspiring Digital Marketers & Career Switchers",
    details: [
      "Beginners with zero marketing experience",
      "Students seeking practical skills",
      "Professionals pivoting to digital marketing",
      "Entrepreneurs wanting to market their own businesses"
    ],
    color: "from-maroon to-maroon-dark"
  },
  {
    question: "WHAT WILL YOU LEARN?",
    icon: Target,
    answer: "Complete Digital Marketing Mastery",
    details: [
      "Week 1: Content Creation & Viral Strategies",
      "Week 2: Video Editing & Production (CapCut, Canva)",
      "Week 3: Meta Ads - Facebook & Instagram Advertising",
      "Week 4: TikTok Ads + WhatsApp Sales Automation"
    ],
    color: "from-[hsl(186,70%,20%)] to-[hsl(186,60%,15%)]"
  },
  {
    question: "WHY CHOOSE US?",
    icon: GraduationCap,
    answer: "Career-Ready in Just 4 Weeks",
    details: [
      "Industry-recognized certification upon completion",
      "Build a professional portfolio with real campaigns",
      "Direct connections to hiring agencies & employers",
      "500+ alumni successfully placed in marketing roles"
    ],
    color: "from-maroon to-maroon-dark"
  },
  {
    question: "HOW DOES IT WORK?",
    icon: Clock,
    answer: "Flexible, Hands-On Training",
    details: [
      "Virtual: Evening classes (Tue-Thu, 8-9:30pm) - KES 13,000",
      "Hybrid: In-person + Online sessions - KES 25,000",
      "Live instruction with real campaign practice",
      "Lifetime access to all course materials & updates"
    ],
    color: "from-[hsl(186,70%,20%)] to-[hsl(186,60%,15%)]"
  },
  {
    question: "WHEN CAN YOU START?",
    icon: Calendar,
    answer: "Next Intake: February 17, 2026",
    details: [
      "Training runs for 4 consecutive weeks",
      "Join any week based on your interest area",
      "Flexible payment: Weekly (KES 3,500) or Full program",
      "Limited spots - Early enrollment recommended"
    ],
    color: "from-maroon to-maroon-dark"
  }
];

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function calculateTimeLeft(): TimeLeft {
  const difference = TRAINING_START_DATE.getTime() - new Date().getTime();
  if (difference <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  
  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / (1000 * 60)) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
}

export default function HeroSectionImproved({ onEnrollClick }: HeroSectionProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft());
  const [activePoster, setActivePoster] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState<Record<number, boolean>>({ 0: true }); // First one open by default

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Auto-play slider effect - changes every 10 seconds
  useEffect(() => {
    if (isPaused) return;

    const autoPlayInterval = setInterval(() => {
      setIsTransitioning(true);
      setActivePoster((prev) => (prev + 1) % POSTERS.length);
      setTimeout(() => setIsTransitioning(false), 800);
    }, 10000);

    return () => clearInterval(autoPlayInterval);
  }, [isPaused]);

  const trainingHasStarted = new Date() >= TRAINING_START_DATE;

  const handlePosterClick = (index: number) => {
    if (index !== activePoster) {
      setIsTransitioning(true);
      setActivePoster(index);
      setIsPaused(true);
      setTimeout(() => {
        setIsTransitioning(false);
        setTimeout(() => setIsPaused(false), 5000);
      }, 800);
    }
  };

  const nextPoster = () => {
    handlePosterClick((activePoster + 1) % POSTERS.length);
  };

  const prevPoster = () => {
    handlePosterClick((activePoster - 1 + POSTERS.length) % POSTERS.length);
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-b from-turquoise-very-dark via-[hsl(186,80%,10%)] to-turquoise-very-dark">
      {/* Premium Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-radial from-turquoise/10 via-transparent to-transparent blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-radial from-maroon/10 via-transparent to-transparent blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>
      
      {/* Header */}
      <header className="relative z-10 border-b border-white/5 backdrop-blur-sm bg-turquoise-very-dark/50">
        <div className="container px-4 md:px-6">
          <div className="flex items-center justify-between py-4 md:py-6">
            <div className="flex items-center gap-2 md:gap-4">
              <img 
                src={janetLogo}
                alt="Janet Marketing Academy" 
                className="h-10 md:h-12 lg:h-14 w-auto"
              />
              <div className="hidden sm:flex flex-col">
                <span className="text-white font-bold text-xs md:text-sm tracking-wide">JANET MARKETING</span>
                <span className="text-turquoise text-[10px] md:text-xs font-medium">ACADEMY</span>
              </div>
            </div>
            
            <Button 
              onClick={onEnrollClick}
              size="sm"
              className="bg-gradient-to-r from-maroon to-maroon-dark hover:from-maroon-dark hover:to-maroon text-white font-bold px-4 md:px-8 text-sm md:text-base shadow-2xl hover:shadow-maroon/30 transition-all duration-300"
            >
              <span className="hidden sm:inline">Enroll Now</span>
              <span className="sm:hidden">Join</span>
              <TrendingUp className="ml-1 md:ml-2 w-4 h-4 md:w-5 md:h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Content */}
      <div className="relative z-10 container px-4 md:px-6 py-6 md:py-12 lg:py-16">
        {/* Countdown Timer */}
        <div className="max-w-2xl mx-auto mb-6 md:mb-10">
          <div className="relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-turquoise to-maroon rounded-xl blur opacity-20" />
            <div className="relative bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] rounded-xl p-4 md:p-6 border border-white/10">
              <div className="text-center mb-4">
                <p className="text-white font-bold text-sm md:text-base mb-1">
                  {trainingHasStarted ? '🎓 Training Active - Join Anytime' : ' February Intake Opening Soon'}
                </p>
                <p className="text-gray-400 text-xs md:text-sm">Limited spots available</p>
              </div>
              
              {!trainingHasStarted && (
                <div className="grid grid-cols-4 gap-2 md:gap-3">
                  {[
                    { value: timeLeft.days, label: 'Days' },
                    { value: timeLeft.hours, label: 'Hrs' },
                    { value: timeLeft.minutes, label: 'Min' },
                    { value: timeLeft.seconds, label: 'Sec' },
                  ].map((item) => (
                    <div key={item.label} className="bg-gradient-to-br from-white/5 to-white/10 rounded-lg p-2 md:p-3 border border-white/10">
                      <div className={`text-xl sm:text-2xl md:text-3xl font-bold text-white text-center mb-1 ${
                        item.label === 'Sec' ? 'tabular-nums' : ''
                      }`}>
                        {String(item.value).padStart(2, '0')}
                      </div>
                      <div className="text-[9px] md:text-[10px] font-medium text-gray-400 text-center uppercase tracking-wider">
                        {item.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-6 md:gap-10 lg:gap-12 items-start max-w-7xl mx-auto">
          {/* LEFT: Value Proposition */}
          <div className="space-y-4 md:space-y-6 order-2 lg:order-1">
            <div className="space-y-3 md:space-y-4">
              <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white leading-[1.15] font-bold">
                Launch Your Digital Marketing Career
                <span className="block mt-1.5 md:mt-2 text-transparent bg-clip-text bg-gradient-to-r from-turquoise via-turquoise-dark to-maroon">
                  In Just 4 Weeks
                </span>
              </h1>
              
              <p className="text-sm md:text-base lg:text-lg text-gray-300 leading-relaxed">
                Master Content Creation, Meta Ads, TikTok Ads & WhatsApp Sales. Get certified and access exclusive job opportunities.
              </p>

              {/* Social Proof */}
              <div className="flex flex-wrap items-center gap-2 md:gap-3 pt-1">
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-emerald-400" />
                  <span className="text-white font-medium text-xs md:text-sm">500+ Graduates</span>
                </div>
                <div className="w-0.5 h-0.5 rounded-full bg-gray-600" />
                <div className="flex items-center gap-1.5">
                  <Briefcase className="w-4 h-4 text-emerald-400" />
                  <span className="text-white font-medium text-xs md:text-sm">Job Placement</span>
                </div>
                <div className="w-0.5 h-0.5 rounded-full bg-gray-600" />
                <div className="flex items-center gap-1.5">
                  <GraduationCap className="w-4 h-4 text-emerald-400" />
                  <span className="text-white font-medium text-xs md:text-sm">Certified</span>
                </div>
              </div>
            </div>

            {/* What You'll Gain */}
            <div className="bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] rounded-xl p-4 md:p-5 border border-white/10">
              <h3 className="text-white font-bold text-sm md:text-base mb-3">What You'll Gain:</h3>
              <div className="space-y-2">
                {[
                  "Professional certification & portfolio",
                  "Hands-on training with real campaigns",
                  "Direct access to hiring managers",
                  "Lifetime course material access"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-turquoise flex-shrink-0 mt-0.5 font-bold">✓</span>
                    <span className="text-xs md:text-sm text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <Button 
                onClick={onEnrollClick}
                size="lg"
                className="w-full bg-gradient-to-r from-maroon via-maroon-dark to-maroon hover:from-maroon-dark hover:via-maroon hover:to-maroon-dark text-white font-bold text-sm md:text-base lg:text-lg px-6 md:px-10 py-5 md:py-6 shadow-2xl hover:shadow-maroon/40 transition-all duration-300 transform hover:scale-[1.02]"
              >
                Reserve Your Spot Now
                <TrendingUp className="ml-2 w-4 h-4 md:w-5 md:h-5" />
              </Button>
              
              <p className="text-gray-400 text-xs md:text-sm text-center">
                <span className="text-turquoise font-semibold">Join 500+ graduates</span> who launched their careers
              </p>
            </div>

            {/* Payment Info */}
            <div className="bg-gradient-to-br from-turquoise/10 to-turquoise-dark/10 rounded-xl p-4 md:p-5 border border-turquoise/20">
              <h3 className="text-white font-bold text-sm md:text-base mb-2">Flexible Payment Options</h3>
              <p className="text-gray-300 text-xs md:text-sm mb-3">
                Pay weekly or monthly. Choose Virtual or Hybrid training.
              </p>
              <div className="flex flex-wrap gap-2 text-xs md:text-sm">
                <Badge className="bg-turquoise/20 text-turquoise border border-turquoise/30">
                  Paybill: 542542
                </Badge>
                <Badge className="bg-turquoise/20 text-turquoise border border-turquoise/30">
                  Account: 544323
                </Badge>
              </div>
            </div>
          </div>

          {/* RIGHT: Context + Posters */}
          <div className="space-y-4 md:space-y-6 order-1 lg:order-2">
            
            {/* Enhanced WHO, WHAT, WHY, HOW, WHEN Section */}
            <div className="bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/10 shadow-2xl">
              <div className="text-center mb-5 md:mb-6">
                <h2 className="text-white font-bold text-xl md:text-2xl mb-2">Everything You Need to Know</h2>
                <p className="text-gray-400 text-sm md:text-base">Your complete program guide at a glance</p>
              </div>

              {/* Desktop: 2-Column Grid */}
              <div className="hidden md:grid md:grid-cols-2 gap-4">
                {CONTEXT_INFO.map((info, index) => {
                  const Icon = info.icon;
                  
                  return (
                    <div 
                      key={index}
                      className={`group relative bg-gradient-to-br from-white/5 to-white/10 rounded-xl p-5 border border-white/10 hover:border-turquoise/50 transition-all duration-300 hover:shadow-xl ${
                        index === CONTEXT_INFO.length - 1 && CONTEXT_INFO.length % 2 !== 0 ? 'md:col-span-2' : ''
                      }`}
                    >
                      {/* Gradient Accent Bar */}
                      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${info.color} rounded-t-xl`} />
                      
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br ${info.color} flex items-center justify-center shadow-lg`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {/* Question */}
                          <h3 className="text-turquoise font-bold text-xs uppercase tracking-wide mb-1">
                            {info.question}
                          </h3>
                          
                          {/* Answer */}
                          <h4 className="text-white font-bold text-base mb-3 leading-tight">
                            {info.answer}
                          </h4>
                          
                          {/* Details List */}
                          <ul className="space-y-2">
                            {info.details.map((detail, i) => (
                              <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                                <span className="text-turquoise flex-shrink-0 mt-0.5 font-bold">•</span>
                                <span className="leading-relaxed">{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Hover Glow Effect */}
                      <div className={`absolute inset-0 bg-gradient-to-r ${info.color} rounded-xl opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`} />
                    </div>
                  );
                })}
              </div>

              {/* Mobile: Accordion Style */}
              <div className="md:hidden space-y-3">
                {CONTEXT_INFO.map((info, index) => {
                  const Icon = info.icon;
                  const isExpanded = expandedAccordions[index] || false;
                  
                  const toggleAccordion = () => {
                    setExpandedAccordions(prev => ({
                      ...prev,
                      [index]: !prev[index]
                    }));
                  };
                  
                  return (
                    <div 
                      key={index}
                      className="bg-gradient-to-br from-white/5 to-white/10 rounded-lg border border-white/10 overflow-hidden"
                    >
                      {/* Header - Always Visible */}
                      <button
                        onClick={toggleAccordion}
                        className="w-full flex items-center gap-3 p-4 text-left transition-colors hover:bg-white/5"
                      >
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${info.color} flex items-center justify-center shadow-lg`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {/* Question */}
                          <h3 className="text-turquoise font-bold text-xs uppercase tracking-wide mb-0.5">
                            {info.question}
                          </h3>
                          
                          {/* Answer */}
                          <h4 className="text-white font-bold text-sm leading-tight">
                            {info.answer}
                          </h4>
                        </div>

                        {/* Expand/Collapse Icon */}
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full bg-white/10 flex items-center justify-center transition-transform duration-300 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}>
                          <ChevronRight className="w-4 h-4 text-white rotate-90" />
                        </div>
                      </button>

                      {/* Expandable Details */}
                      <div 
                        className={`transition-all duration-300 ease-in-out ${
                          isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        } overflow-hidden`}
                      >
                        <div className="px-4 pb-4 pt-2">
                          <ul className="space-y-2">
                            {info.details.map((detail, i) => (
                              <li key={i} className="flex items-start gap-2 text-gray-300 text-xs">
                                <span className="text-turquoise flex-shrink-0 mt-0.5 font-bold">•</span>
                                <span className="leading-relaxed">{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Bottom Accent Line */}
                      {isExpanded && (
                        <div className={`h-1 bg-gradient-to-r ${info.color}`} />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Bottom CTA */}
              <div className="mt-6 pt-5 border-t border-white/10 text-center">
                <p className="text-gray-400 text-sm mb-3">
                  Ready to transform your career?
                </p>
                <button
                  onClick={onEnrollClick}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-maroon to-maroon-dark hover:from-maroon-dark hover:to-maroon text-white font-bold text-sm rounded-lg shadow-lg hover:shadow-maroon/30 transition-all duration-300 hover:scale-105"
                >
                  <span>View Training Formats Below</span>
                  <TrendingUp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Poster Carousel Section */}
            <div className="space-y-3 md:space-y-4">
              <div className="text-center">
                <h3 className="text-white font-bold text-base md:text-lg mb-1">Choose Your Training Format</h3>
                <p className="text-gray-400 text-xs md:text-sm">View all program details below</p>
              </div>

              {/* Thumbnail Preview Strip */}
              <div className="flex items-center justify-center gap-2 md:gap-3 px-2">
                {POSTERS.map((poster, index) => (
                  <button
                    key={poster.id}
                    onClick={() => handlePosterClick(index)}
                    className={`relative group flex-1 transition-all duration-700 ease-in-out ${
                      activePoster === index 
                        ? 'scale-92 opacity-40' 
                        : 'scale-100 opacity-100 hover:scale-105'
                    }`}
                  >
                    <div className={`relative overflow-hidden rounded-lg md:rounded-xl border-2 transition-all duration-300 ${
                      activePoster === index
                        ? 'border-maroon shadow-lg shadow-maroon/30'
                        : 'border-white/10 group-hover:border-turquoise/50'
                    }`}>
                      {poster.popular && (
                        <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 z-10">
                          <Badge className="bg-turquoise text-white text-[8px] md:text-[9px] font-bold px-1.5 py-0.5">
                            POPULAR
                          </Badge>
                        </div>
                      )}
                      
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <img 
                          src={poster.image}
                          alt={poster.title}
                          className="w-full h-full object-cover"
                        />
                        
                        {activePoster !== index && (
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent group-hover:from-black/40 transition-all duration-300" />
                        )}
                        
                        {activePoster === index && (
                          <div className="absolute inset-0 bg-maroon/20 animate-pulse" />
                        )}
                      </div>
                      
                      <div className={`absolute bottom-0 left-0 right-0 px-2 py-1.5 md:py-2 text-center backdrop-blur-sm ${
                        activePoster === index 
                          ? 'bg-maroon/90' 
                          : 'bg-black/70 group-hover:bg-turquoise/80'
                      } transition-all duration-300`}>
                        <p className={`text-[10px] md:text-xs font-bold text-white mb-0.5`}>
                          {poster.title}
                        </p>
                        <p className={`text-[8px] md:text-[10px] ${
                          activePoster === index ? 'text-turquoise' : 'text-gray-300'
                        }`}>
                          {poster.badge}
                        </p>
                      </div>

                      {activePoster !== index && (
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="bg-white/90 rounded-full px-3 py-1.5">
                            <span className="text-[10px] md:text-xs font-bold text-gray-900">
                              Click to View
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Main Poster Display */}
              <div className="relative" style={{ minHeight: '500px' }}>
                <div className={`absolute -inset-1 bg-gradient-to-br from-turquoise/30 to-maroon/30 rounded-2xl blur-lg transition-opacity duration-700 ${
                  isTransitioning ? 'opacity-80' : 'opacity-50'
                }`} />
                
                <div className="relative bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] rounded-xl md:rounded-2xl p-3 md:p-4 border border-white/10">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-maroon to-maroon-dark text-white text-xs md:text-sm font-bold px-4 py-1.5 shadow-lg">
                      {POSTERS[activePoster].title}
                    </Badge>
                  </div>

                  <div 
                    key={activePoster}
                    className="relative rounded-lg md:rounded-xl overflow-hidden"
                    style={{ animation: 'fadeInScale 1s ease-in-out forwards' }}
                  >
                    <img 
                      src={POSTERS[activePoster].image}
                      alt={POSTERS[activePoster].title}
                      className="w-full h-auto object-contain"
                    />
                    
                    <button
                      onClick={prevPoster}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/70 hover:bg-black/90 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:scale-110 active:scale-95"
                      aria-label="Previous poster"
                    >
                      <ChevronLeft className="w-6 h-6 text-white" />
                    </button>
                    
                    <button
                      onClick={nextPoster}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/70 hover:bg-black/90 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:scale-110 active:scale-95"
                      aria-label="Next poster"
                    >
                      <ChevronRight className="w-6 h-6 text-white" />
                    </button>
                  </div>

                  <div className="flex items-center justify-center gap-2 mt-4 md:mt-5">
                    {POSTERS.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => handlePosterClick(index)}
                        className={`h-2 rounded-full transition-all duration-500 ${
                          activePoster === index
                            ? 'w-8 md:w-10 bg-gradient-to-r from-maroon to-turquoise shadow-lg'
                            : 'w-2 bg-white/30 hover:bg-white/50 hover:w-4'
                        }`}
                        aria-label={`View ${POSTERS[index].title}`}
                      />
                    ))}
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="flex items-center justify-between text-xs md:text-sm">
                      <span className="text-gray-400">Viewing:</span>
                      <span className="text-turquoise font-bold">{POSTERS[activePoster].badge}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-center">
                  <p className="text-gray-400 text-xs md:text-sm flex items-center justify-center gap-2">
                    <span className="animate-pulse">●</span>
                    <span className="hidden md:inline">Auto-rotating every 10s • Click thumbnails to view instantly</span>
                    <span className="md:hidden">Auto-playing • Tap to view</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Contact */}
            <div className="bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] rounded-xl p-4 border border-white/10 text-center">
              <p className="text-gray-400 text-xs md:text-sm mb-2">Need help? Call or WhatsApp</p>
              <a 
                href="tel:+254704544323"
                className="text-turquoise font-bold text-base md:text-lg hover:text-turquoise-dark transition"
              >
                +254 704 544 323
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInScale {
          0% {
            opacity: 0;
            transform: scale(0.85) translateY(-40px);
            filter: blur(4px);
          }
          50% {
            opacity: 0.6;
            transform: scale(0.92) translateY(-20px);
            filter: blur(2px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
            filter: blur(0);
          }
        }
        
        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }
      `}</style>
    </section>
  );
}